<script setup lang="ts">
import { ref, computed, watch, inject, onMounted, onBeforeUnmount } from 'vue';
import Checkbox from '../elements/Checkbox.vue';
import { FormCheckBoxEmits, FormCheckboxProps, FormCheckboxRef } from '@/types/form/form-checkbox';
import { DynamicFormContext } from '@/types/form/dynamic-form';
import FormElMsg from '@/form/other/FormElMsg.vue';
import { FormElMsgData } from '@/types/form/others/form-el-msg';
import SiteRichText from '@/elements/SiteRichText.vue';

const props = defineProps<FormCheckboxProps>();
const emit = defineEmits<FormCheckBoxEmits>();
const FIELD_TYPE = 'form-checkbox';
const formContext = inject<DynamicFormContext>('formContext')!;
const rootEl = ref(null);
const getCheckedValue = (value: unknown) =>
  value === true || value === 'true' || value === 1 || value === '1';
const checked = ref(getCheckedValue(props.item?.fields?.checked?.value));
const isRequired = ref(props.item?.fields?.required?.value ?? false);
const isDisabled = ref(false);
const isVisible = ref(true);
const msg = ref<FormElMsgData>();
const fields = computed(() => props.item?.fields);
const fieldName = computed(() => fields.value?.fieldName?.value || '');

// Reset error when checked state changes
const onCheckboxChange = () => {
  msg.value = undefined;
  emit('change', FIELD_TYPE, fieldName.value, checked.value, checked.value);
};

const onTextClick = (e: any) => {
  if (isDisabled.value) return;
  if (e.target && e.target.tagName !== 'A') {
    checked.value = !checked.value;
    onCheckboxChange();
  }
};

const setRequired = (required: boolean) => {
  isRequired.value = required;
};

const setDisabled = (disabled: boolean) => {
  isDisabled.value = disabled;
};

const setVisible = (visible: boolean) => {
  isVisible.value = visible;
};

const updateValue = (newValue?: string | null) => {
  checked.value = getCheckedValue(newValue);
  onCheckboxChange();
};

const validate = async () => {
  const result = {
    value: checked.value,
    valid: true,
    message: '',
  };

  if (!isVisible.value) {
    return result;
  }

  if (isRequired.value && !checked.value) {
    result.valid = false;
    result.message = fields.value?.requiredMsg?.value || 'This field is required';
    msg.value = {
      type: 'error',
      message: result.message,
    };
    return result;
  }
  msg.value = undefined;
  return result;
};

const getRefData = (): FormCheckboxRef => ({
  el: rootEl.value,
  type: 'form-checkbox',
  name: fieldName.value,
  id: props.item.id,
  visible: isVisible.value,
  setRequired,
  setDisabled,
  setVisible,
  updateValue,
  validate,
  getValue: () => checked.value,
});

// Register this component with parent
watch(
  () => props.item,
  () => {
    checked.value = getCheckedValue(props.item?.fields?.checked?.value);
    isRequired.value = props.item?.fields?.required?.value ?? false;
    formContext.regFormEl(getRefData());
  },
  { immediate: true }
);
onMounted(() => {
  formContext.regFormEl(getRefData());
});
onBeforeUnmount(() => {
  formContext.unRegFormEl(getRefData());
});
defineExpose(getRefData());
</script>
<template>
  <div
    :class="['f-checkbox', { disabled: isDisabled, 'has-error': msg?.type === 'error' }]"
    v-if="isVisible"
    ref="rootEl"
  >
    <Checkbox v-model:value="checked" :disabled="isDisabled" @change="onCheckboxChange" />
    <div class="f-checkbox__text" @click="onTextClick">
      <site-rich-text v-if="fields?.mandatoryText" :field="fields.mandatoryText" />
      <form-el-msg :msg="msg" />
    </div>
  </div>
</template>

<style lang="scss">
@import '../styles/variables';
@import '../styles/functions';
@import '../styles/mixins';

.f-checkbox {
  display: flex;
  gap: 16px;
  user-select: none;
  .e-form-checkbox {
    margin-top: 2px;
  }

  &__text {
    display: flex;
    flex-direction: column;
    gap: 0;
    flex: 1;
    font-family: 'Figtree', sans-serif;
    font-size: 14px;
    line-height: 1.4;
    cursor: pointer;
    p {
      margin: 0;
    }
    a {
      color: $color-trusted-blue;
      text-decoration: underline;
      &:hover {
        color: $color-trusted-blue-hover;
      }
    }
  }

  &__error {
    font-size: 12px;
    color: #dc3545;
    margin-top: 4px;
  }

  &.disabled {
    opacity: 0.6;
    .f-checkbox__text {
      cursor: not-allowed;
    }
  }
}
</style>
